import React, { useState, useEffect } from "react";
import { withField, Cascader } from "@douyinfe/semi-ui";
import type {
  Entity,
  CascaderData,
  Value,
} from "@douyinfe/semi-ui/lib/es/cascader";
import { ANTIBODY_DESIGN_TYPE_MAP } from "@services/home/config/home.config";
import {
  JobTypeEnum,
  AntibodyDesignBinderTypeEnum,
  PeptideDesignBinderTypeEnum,
} from "@services/home/enum/home.enum";
import { useTranslation } from "react-i18next";
import { ReactComponent as DeNovoIcon } from "@assets/icons/de_novo_icon.svg";
import { ReactComponent as DockingIcon } from "@assets/icons/docking_icon.svg";
import { ReactComponent as PeptideIcon } from "@assets/icons/peptide_icon.svg";

import styles from "./index.module.scss";

interface Prop {
  value?: string;
  style?: Record<string, string>;
  onChange?: (values: any) => void;
}

const JobTypeCascader: React.FC<Prop> = ({ value, style, onChange }) => {
  const [selectValue, setSelectValue] = useState<string[]>([]);
  const { t, i18n } = useTranslation();

  const getJobLabel = (type: JobTypeEnum): string => t(`jobType.${type}`);

  const getJobDesc = (type: JobTypeEnum): string => t(`jobDescription.${type}`);

  const getPepideLabel = (type: PeptideDesignBinderTypeEnum): string =>
    t(`peptideDesignType.${type}`);

  const JOB_TYPE_TREE: CascaderData[] = [
    {
      label: (
        <div className={styles.row}>
          <div className={styles.iconContainer}>
            <DeNovoIcon className={`${styles.icon} job-icon`} />
          </div>
          <div className={styles.content}>
            <div className={styles.title}>
              {getJobLabel(JobTypeEnum.VhhDesign)}
            </div>
            <div className={styles.desc}>
              {getJobDesc(JobTypeEnum.VhhDesign)}
            </div>
          </div>
        </div>
      ),
      value: JobTypeEnum.VhhDesign,
      children: [
        {
          label: ANTIBODY_DESIGN_TYPE_MAP[AntibodyDesignBinderTypeEnum.Vhh],
          value: `${JobTypeEnum.VhhDesign}-${AntibodyDesignBinderTypeEnum.Vhh}`,
        },
        {
          label: ANTIBODY_DESIGN_TYPE_MAP[AntibodyDesignBinderTypeEnum.Scfv],
          value: `${JobTypeEnum.VhhDesign}-${AntibodyDesignBinderTypeEnum.Scfv}`,
        },
      ],
    },
    {
      label: (
        <div className={styles.row}>
          <div className={styles.iconContainer}>
            <PeptideIcon className={`${styles.icon} job-icon`} />
          </div>
          <div className={styles.content}>
            <div className={styles.title}>
              {getJobLabel(JobTypeEnum.PeptideDesignLite)}
            </div>
            <div className={styles.desc}>
              {getJobDesc(JobTypeEnum.PeptideDesignLite)}
            </div>
          </div>
        </div>
      ),
      value: JobTypeEnum.PeptideDesignLite,
      children: [
        {
          label: getPepideLabel(PeptideDesignBinderTypeEnum.LinPep),
          value: `${JobTypeEnum.PeptideDesignLite}-${PeptideDesignBinderTypeEnum.LinPep}`,
        },
        {
          label: getPepideLabel(PeptideDesignBinderTypeEnum.DisulPep),
          value: `${JobTypeEnum.PeptideDesignLite}-${PeptideDesignBinderTypeEnum.DisulPep}`,
        },
        {
          label: getPepideLabel(PeptideDesignBinderTypeEnum.CycPep),
          value: `${JobTypeEnum.PeptideDesignLite}-${PeptideDesignBinderTypeEnum.CycPep}`,
        },
      ],
    },
    //   {
    //     label: (
    //       <div className={styles.row}>
    //         <div className={styles.iconContainer}>
    //           <BinderIcon className={`${styles.icon} job-icon`} />
    //         </div>
    //         <div className={styles.content}>
    //           <div className={styles.title}>
    //             {getJobLabel(JobTypeEnum.ProteinBinderDesign)}
    //           </div>
    //           <div className={styles.desc}>
    //             Design a protein binder de novo to a target biomolecule
    //           </div>
    //         </div>
    //       </div>
    //     ),
    //     value: JobTypeEnum.ProteinBinderDesign,
    //   },
    {
      label: (
        <div className={styles.row}>
          <div className={styles.iconContainer}>
            <DockingIcon className={`${styles.icon} job-icon`} />
          </div>
          <div className={styles.content}>
            <div className={styles.title}>
              {getJobLabel(JobTypeEnum.AbAgDocking)}
            </div>
            <div className={styles.desc}>
              {getJobDesc(JobTypeEnum.AbAgDocking)}
            </div>
          </div>
        </div>
      ),
      value: JobTypeEnum.AbAgDocking,
    },
  ];
  function displayRender(selected: string[] | Entity) {
    if (Array.isArray(selected)) {
      const list = selected.map((item) => {
        if (React.isValidElement(item)) {
          const props = item.props as { children: React.ReactElement[] };
          const target = props.children[1];
          const targetProps = target.props as {
            children: React.ReactElement[];
          };
          const targetNode = targetProps.children[0].props as {
            children: string;
          };

          return targetNode.children;
        } else {
          return item;
        }
      });

      if (list.length > 1) {
        return `${list[0]} (${list[1]})`;
      }

      return list.join("-");
    }

    return "-";
  }

  function onTypeChange(v: Value) {
    if (Array.isArray(v)) {
      setSelectValue(v as string[]);
      if (onChange) {
        const val = v[v.length - 1];
        onChange(val);
      }
    }
  }

  useEffect(() => {
    if (value?.includes("-")) {
      const arr = value.split("-");
      const v = [arr[0], value];
      setSelectValue(v);
    } else if (value) {
      setSelectValue([value]);
    }
  }, [value]);

  return (
    <Cascader
      className={styles.selectContainer}
      showNext="hover"
      dropdownClassName={styles.cascaderContainer}
      value={selectValue}
      style={style}
      treeData={JOB_TYPE_TREE}
      onChange={onTypeChange}
      displayRender={displayRender}
    />
  );
};

export default withField(JobTypeCascader);
